import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { CustomEvent, Map } from "schemas/functions";
import { routes } from "schemas/routes";

import { isPage } from "utils/general";
import { makeDeepCopy } from "utils/updateLocalState";

import styles from "./styles";

enum IncomeMethod {
  getting_started = "I'm just getting started (not monetizing yet)",
  brand_deals = "Brand deals & sponsorships",
  affiliate_marketing = "Affiliate marketing",
  selling_my_own_products = "Selling my own products (physical or digital)",
  paid_subscriptions = "Paid subscriptions or memberships",
  platform_payouts = "Platform payouts / ad revenue",
  live_streaming = "Live streaming tips or donations",
  other = "Other",
}

const MONTHLY_INCOME_OPTIONS = [
  {
    min: 0,
    max: 0,
    label: "$0",
  },
  {
    min: 1,
    max: 1000,
    label: "$1 - $1,000",
  },
  {
    min: 1001,
    max: 10000,
    label: "$1,001 - $10,000",
  },
  {
    min: 1001,
    max: 25000,
    label: "$10,001 - $25,000",
  },
  {
    min: 25001,
    max: 25001,
    label: "$25,001+",
  },
];

interface IncomeFormProps {
  handleNext: (onboardingForm: Map) => void;
  handleSkip?: () => void;
  isProfilePage?: boolean;
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  handleBack?: () => void;
}

const IncomeForm = ({
  handleNext,
  handleSkip,
  onboardingForm,
  setOnboardingForm,
  handleBack,
  isProfilePage = false,
}: IncomeFormProps) => {
  const handleChange = (e: CustomEvent) => {
    const { value, checked } = e.target;
    setOnboardingForm((prev) => {
      const currentMethods = prev.incomeMethod || [];
      let updatedMethods = [...currentMethods];
      if (checked) {
        updatedMethods.push(value);
      } else {
        updatedMethods = updatedMethods.filter(
          (method: string) => method !== value,
        );
      }
      return { ...prev, incomeMethod: updatedMethods };
    });
  };

  const handleUpdateList = (e: CustomEvent) => {
    const { value } = e.target;
    setOnboardingForm((prev) => {
      const copy = makeDeepCopy(prev);

      const selectedValue = MONTHLY_INCOME_OPTIONS.find(
        (option) => option.label === value,
      );
      copy.monthlyIncome = [selectedValue?.min, selectedValue?.max];
      return copy;
    });
  };

  return (
    <>
      <Typography variant="h3" sx={{ mb: 3, textAlign: "center" }}>
        <Box
          component="i"
          color="secondary.dark"
          sx={{ pr: 2 }}
          className="fa-solid fa-dollar-sign"
        />
        Your Income
      </Typography>

      <Typography>
        How are you currently earning as a creator?{" "}
        <em>(Select all that apply)</em>
      </Typography>

      <FormGroup onChange={handleChange}>
        {Object.values(IncomeMethod).map((method) => (
          <FormControlLabel
            key={method}
            control={
              <Checkbox
                checked={onboardingForm?.incomeMethod?.includes(method)}
              />
            }
            label={method}
            value={method}
          />
        ))}
      </FormGroup>

      {onboardingForm?.incomeMethod?.includes(IncomeMethod.other) && (
        <TextField
          fullWidth
          label="Other (please specify)"
          sx={[{ mt: 2, mb: 3 }, styles.textField]}
          value={onboardingForm?.incomeMethodOther || ""}
          onChange={(e) =>
            setOnboardingForm((prev) => ({
              ...prev,
              incomeMethodOther: e.target.value,
            }))
          }
        />
      )}

      <Typography>
        About how much revenue did you earn from your creator business last
        month?
      </Typography>

      {MONTHLY_INCOME_OPTIONS.map((option) => (
        <FormControl onChange={handleUpdateList}>
          <RadioGroup defaultValue="female" name="radio-buttons-group">
            {
              <FormControlLabel
                value={option.label}
                control={<Radio />}
                label={option.label}
                checked={
                  onboardingForm?.monthlyIncome?.[0] === option.min &&
                  onboardingForm?.monthlyIncome?.[1] === option.max
                }
              />
            }
          </RadioGroup>
        </FormControl>
      ))}

      <Grid sx={styles.fieldContainer}></Grid>

      {!isProfilePage && (
        <Grid container>
          <Grid item xs>
            <Button color="secondary" onClick={handleBack}>
              Back
            </Button>
          </Grid>
          {handleSkip && (
            <Grid item xs="auto">
              <Button onClick={handleSkip} color="secondary" disableElevation>
                Skip
              </Button>
            </Grid>
          )}
          <Grid item xs="auto">
            <Button
              disableElevation
              variant={
                isPage(routes.aboutSelfServeOnboarding)
                  ? undefined
                  : "contained"
              }
              onClick={() => handleNext(onboardingForm)}
            >
              Next
              {isPage(routes.aboutSelfServeOnboarding) && (
                <Box
                  component="i"
                  className="fa-regular fa-arrow-right"
                  sx={{ ml: 1 }}
                />
              )}
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default IncomeForm;
